import { Box, TextField, Typography, Chip, InputAdornment, List, useTheme } from "@mui/material";
import { useSelector, shallowEqual } from "react-redux";
import { useState, useMemo } from "react";
import { Icon } from '@iconify/react';
import WhatsAppTemplateSelector from "src/components/whatsappComponents/WhatsAppTemplateSelector";
import WhatsAppTemplateListItem from "src/components/whatsappComponents/WhatsAppTemplateListItem";
import { formatUsageType, getUsageTypeColor } from "src/components/whatsappComponents/whatsAppTemplateUtils";
import PropTypes from "prop-types";

const WhatsappTemplateListV2 = ({ onTemplateSelect }) => {
    const theme = useTheme();
    const { allWhatsappTemplateLoading, allWhatsappTemplates } = useSelector((state) => ({
        allWhatsappTemplateLoading: state.campaign?.allWhatsappTemplateLoading,
        allWhatsappTemplates: state.campaign?.allWhatsappTemplates
    }), shallowEqual);

    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [openPreview, setOpenPreview] = useState(false);

    // Get unique usage types for filter tabs
    const usageTypes = useMemo(() => {
        const types = new Set((allWhatsappTemplates || []).map(t => t.usage_type).filter(Boolean));
        return ['ALL', ...Array.from(types)];
    }, [allWhatsappTemplates]);

    // Get counts for each usage type
    const usageTypeCounts = useMemo(() => {
        const templates = allWhatsappTemplates || [];
        const counts = { ALL: templates.length };
        templates.forEach(t => {
            const type = t.usage_type || 'GENERAL';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [allWhatsappTemplates]);

    // Filter templates based on search and category
    const filteredTemplates = useMemo(() => {
        let filtered = allWhatsappTemplates || [];

        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(t =>
                t.name?.toLowerCase().includes(searchLower) ||
                t.components?.some(c => c.text?.toLowerCase().includes(searchLower))
            );
        }

        if (selectedCategory !== 'ALL') {
            filtered = filtered.filter(t => t.usage_type === selectedCategory);
        }

        return filtered;
    }, [allWhatsappTemplates, searchText, selectedCategory]);

    const handleTemplateClick = (template) => {
        setSelectedTemplate(template);
        setOpenPreview(true);
        onTemplateSelect?.(template);
    };

    const handleSendFromModal = (template) => {
        onTemplateSelect?.(template);
        setOpenPreview(false);
    };

    return (
        <Box>
            {/* Search */}
            <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Icon icon="mdi:magnify" width={20} color={theme.palette.text.disabled} />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 1.5 }}
            />

            {/* Category Tabs */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 2,
                    overflowX: 'auto',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {usageTypes.map((type) => {
                    const isActive = selectedCategory === type;
                    const chipColor = type === 'ALL'
                        ? { bg: theme.palette.primary.main, color: '#fff' }
                        : getUsageTypeColor(type, theme);
                    return (
                        <Chip
                            key={type}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <span>{type === 'ALL' ? 'All' : formatUsageType(type)}</span>
                                    <Box
                                        component="span"
                                        sx={{
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            bgcolor: isActive
                                                ? 'rgba(255,255,255,0.3)'
                                                : theme.palette.grey[200],
                                            color: isActive
                                                ? 'inherit'
                                                : theme.palette.text.secondary,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            minWidth: 20,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {usageTypeCounts[type] || 0}
                                    </Box>
                                </Box>
                            }
                            onClick={() => setSelectedCategory(type)}
                            variant={isActive ? 'filled' : 'outlined'}
                            sx={{
                                fontWeight: 500,
                                ...(isActive ? {
                                    bgcolor: type === 'ALL' ? theme.palette.primary.main : chipColor.color,
                                    color: '#fff',
                                    borderColor: 'transparent',
                                    '&:hover': {
                                        bgcolor: type === 'ALL' ? theme.palette.primary.dark : chipColor.color,
                                    },
                                    '&.MuiChip-filled': {
                                        bgcolor: type === 'ALL' ? theme.palette.primary.main : chipColor.color,
                                        color: '#fff',
                                    },
                                } : {
                                    borderColor: theme.palette.grey[300],
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        bgcolor: theme.palette.grey[100],
                                    },
                                }),
                            }}
                        />
                    );
                })}
            </Box>

            {/* Template List */}
            {allWhatsappTemplateLoading && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Loading templates...</Typography>
                </Box>
            )}
            {!allWhatsappTemplateLoading && filteredTemplates.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Icon icon="mdi:file-document-outline" width={48} color={theme.palette.grey[300]} />
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No templates found
                    </Typography>
                </Box>
            )}
            {!allWhatsappTemplateLoading && filteredTemplates.length > 0 && (
                <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
                    <List disablePadding sx={{ maxHeight: 500, overflowY: 'auto' }}>
                        {filteredTemplates.map((template) => (
                            <WhatsAppTemplateListItem
                                key={template.id}
                                template={template}
                                isSelected={selectedTemplate?.id === template.id}
                                onClick={() => handleTemplateClick(template)}
                            />
                        ))}
                    </List>
                </Box>
            )}

            {/* Preview Modal */}
            <WhatsAppTemplateSelector
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                templates={allWhatsappTemplates || []}
                selectedTemplate={selectedTemplate}
                onSelect={(template) => {
                    setSelectedTemplate(template);
                    onTemplateSelect?.(template);
                }}
                onSend={handleSendFromModal}
                loading={allWhatsappTemplateLoading}
                title="Template Preview"
            />
        </Box>
    );
}

WhatsappTemplateListV2.propTypes = {
    onTemplateSelect: PropTypes.func
}

export default WhatsappTemplateListV2;
